import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { ExxComError } from 'lib/classes/exxcom-error.class';
import { RouterService } from 'lib/services/router.service';
import { waitFor } from 'lib/tools';

const scriptName = 'marketo.service';

declare let MktoForms2: any; // https://developers.marketo.com/javascript-api/forms/api-reference/
declare let Munchkin: any; // https://developers.marketo.com/javascript-api/lead-tracking/
declare global {
    interface Window {
        munchkinConsent: {
            granted: boolean;
            initialized: boolean;
        };
    }
}

@Injectable()
export class MarketoService {
    private config: any;
    private environment: any;
    private routerService: any;

    isBrowser: boolean;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject('environment') e: any,
        r: RouterService
    ) {
        try {
            this.isBrowser = isPlatformBrowser(this.platformId);
            this.config = e.marketo;
            this.environment = e;
            this.routerService = r;
        } catch (err) {
            console.error(...new ExxComError(420394, scriptName, err).stamp());
        }
    }

    private munchkinInitialized = false;

    async init(): Promise<void> {
        try {
            if (!this.isBrowser) {
                return;
            }

            const scriptsReady = () => !!MktoForms2 && !!Munchkin;
            if (!(await waitFor(scriptsReady, '3s'))) {
                // Termly can report analytics as allowed while never having released the tags it was
                // meant to release — its unblocking pass runs once and can finish before the regional
                // default resolves. Activate what it left behind, then give the scripts another chance.
                if (!this.activateBlockedAnalyticsTags()) {
                    return;
                }
                if (!(await waitFor(scriptsReady, '3s'))) {
                    return;
                }
            }

            // Forms configuration - always set up (doesn't require consent)
            MktoForms2.setOptions({
                formXDPath: `/rs/${this.config.munchkin.id}/images/marketo-xdframe-relative.html`,
            });

            // Munchkin initialization - only if analytics consent granted
            const hasConsent = await this.checkAnalyticsConsent();
            if (hasConsent) {
                if (!this.munchkinInitialized) {
                    this.initMunchkin();
                }
            } else {
                // Listen for consent to be granted later
                this.listenForAnalyticsConsent();
            }
        } catch (err) {
            console.error(...new ExxComError(902837, scriptName, err).stamp());
        }
    }

    /**
     * @function activateBlockedAnalyticsTags
     * @description Last-resort recovery for analytics tags Termly authorised but never released.
     * Termly's resource blocker makes a single unblocking pass; when its consent state resolves after
     * that pass — a regional opt-out default is the common case — it reports analytics as permitted
     * while the `text/plain` tags are still inert, and nothing re-scans. Measured on 6 of 18
     * default-allowed leads, which submitted with `window.Munchkin` undefined.
     *
     * Only ever runs when Termly itself says analytics is permitted, and only after the script wait in
     * init() has already failed, so healthy sessions never reach it.
     * @returns {boolean} Whether any blocked tag was activated.
     */
    private activateBlockedAnalyticsTags(): boolean {
        try {
            if (!(window as any).Termly?.getConsentState?.()?.analytics) {
                return false;
            }

            const blocked = document.querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-categories*="analytics"]');
            if (!blocked.length) {
                return false;
            }

            blocked.forEach((original) => {
                const activated = document.createElement('script');
                // Dropping data-categories stops Termly from re-blocking the clone; every other
                // attribute is preserved because ids are looked up (teknklFormsPlus)
                Array.from(original.attributes)
                    .filter((attr) => attr.name !== 'type' && attr.name !== 'data-categories')
                    .forEach((attr) => activated.setAttribute(attr.name, attr.value));

                if (!original.src) {
                    activated.textContent = original.textContent;
                }

                // Insert the clone, then drop the original so a later Termly pass finds nothing to
                // release. That is the double-fire guard, and it makes repeat calls idempotent —
                // init() runs on every route change.
                original.parentNode?.insertBefore(activated, original);
                original.remove();
            });

            console.log(`Termly left ${blocked.length} analytics tag(s) blocked despite consent - activated`);
            return true;
        } catch (err) {
            console.error(...new ExxComError(902838, scriptName, err).stamp());
            return false;
        }
    }

    private async checkAnalyticsConsent(): Promise<boolean> {
        // Check if consent was already granted via Termly
        if (window.munchkinConsent?.granted) {
            return true;
        }

        // An explicit opt-out always wins, even if Termly reports analytics as allowed.
        // Marketo sets 'true', but check for any truthy value to be safe
        const optOut = document.cookie.match(/mkto_opt_out=([^;]+)/);
        if (optOut && optOut[1] && optOut[1] !== 'false' && optOut[1] !== '0') {
            return false;
        }

        // Fall back to Termly directly. The bridge in index.html only flips munchkinConsent.granted
        // from an event, so a regional default-allow is missed when init() runs before it fires —
        // this keeps the gate on the same source of truth as getTermlyConsentState().
        return !!(window as any).Termly?.getConsentState?.()?.analytics;
    }

    /**
     * @function getTermlyConsentState
     * @description Returns the visitor's cookie-consent state for form submissions.
     * 'accepted' / 'declined' reflect an explicit banner choice, persisted to
     * localStorage by the Termly bridge in index.html. Without an explicit choice,
     * Termly's effective regional default splits 'default-allowed' (e.g. US opt-out)
     * from 'unanswered' (opt-in region, or Termly blocked/not yet initialized).
     */
    getTermlyConsentState(): string {
        try {
            if (!this.isBrowser) return 'unanswered';

            const explicitChoice = localStorage.getItem('termly_explicit_choice');
            if (explicitChoice === 'accepted' || explicitChoice === 'declined') {
                return explicitChoice;
            }

            const termly = (window as any).Termly;
            if (termly?.getConsentState?.()?.analytics) {
                return 'default-allowed';
            }

            return 'unanswered';
        } catch (err) {
            return 'unanswered';
        }
    }

    private initMunchkin(): void {
        if (this.munchkinInitialized) {
            return;
        }

        // Remove opt-out cookie if it exists (user re-consented). It may be on the host or on the
        // registrable domain (the decline path and the configurator write it there, ST-3778).
        const expired = 'mkto_opt_out=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        document.cookie = expired;
        const hostParts = location.hostname.split('.');
        if (hostParts.length > 2) {
            document.cookie = `${expired}; domain=.${hostParts.slice(-2).join('.')}`;
        }

        Munchkin.init(this.config.munchkin.id, this.config.munchkin.settings);
        this.munchkinInitialized = true;
        console.log('Munchkin: Initialized with analytics consent');
    }

    private listenForAnalyticsConsent(): void {
        // Listen for consent granted event
        const consentHandler = () => {
            if (!this.munchkinInitialized) {
                this.initMunchkin();
            }
            window.removeEventListener('munchkin-consent-granted', consentHandler);
        };
        window.addEventListener('munchkin-consent-granted', consentHandler);

        // Listen for consent revoked event (for future page loads - current session handled by mkto_opt_out cookie)
        window.addEventListener('munchkin-consent-revoked', () => {
            console.log('Analytics consent revoked - Munchkin tracking stopped');
        });
    }

    async initLoaded() {
        if (document.readyState === 'complete') {
            // The page is fully loaded.
            setTimeout(() => {
                this.loaded();
            }, 200);
        }
    }

    async loaded() {
        try {
            if (!this.isBrowser || typeof Munchkin === 'undefined' || !Munchkin || !this.munchkinInitialized) {
                return;
            }

            const arrayify = getSelection.call.bind([].slice);

            const sources = {
                breadcrumb: document.querySelector("div[id$='-breadcrumb']"),
                meta: document.querySelector('meta[name=collection]') as HTMLMetaElement,
            };

            // console.log('DEBUG: Munchkin supplemental sources', sources);
            const hitBaseURL = '/_supplemental/v1/collection/';
            let hitComponents = [];

            if (sources.breadcrumb) {
                const bcContent = arrayify(sources.breadcrumb.querySelectorAll('a')).map(function (bcLink) {
                    return bcLink.textContent.trim();
                });

                hitComponents = hitComponents.concat(bcContent);
            }

            if (sources.meta) {
                hitComponents = hitComponents.concat(sources.meta.content);
            }

            if (hitComponents.length && hitComponents[0] != '') {
                Munchkin.munchkinFunction('visitWebPage', {
                    url: hitBaseURL + hitComponents.join(','),
                });
                localStorage.setItem('breadcrumb', hitBaseURL + hitComponents.join(','));
            } else {
                Munchkin.munchkinFunction('visitWebPage', {
                    url: await this.routerService.router.url,
                });
            }
        } catch (err) {
            console.error(...new ExxComError(923847, scriptName, err).stamp());
        }
    }

    sendConfigurator(breadcrumb: string) {
        try {
            if (!this.isBrowser || typeof Munchkin === 'undefined' || !Munchkin || !this.munchkinInitialized) {
                return;
            }
            Munchkin.munchkinFunction('visitWebPage', { url: breadcrumb });
        } catch (err) {
            console.error(...new ExxComError(829374, scriptName, err).stamp());
        }
    }

    /*
  this helper function can be used to retrieve marketo cookie and sync 
  anonymous leads if needed
  */
    getMarketoCookie() {
        if (!this.isBrowser) {
            return;
        }
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const name = cookies[i].split('=')[0].toLowerCase();
            const value = cookies[i].split('=')[1].toLowerCase();
            if (name === ' _mkto_trk') {
                return value;
            } else if (value === ' _mkto_trk') {
                return name;
            }
        }
        return '';
    }

    async load(formId: number | string): Promise<void> {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    if (!this.isBrowser) {
                        resolve();
                        return;
                    }
                    // Wait for MktoForms2 to exist and be functional
                    if (!(await waitFor(() => !!MktoForms2 && typeof MktoForms2.loadForm === 'function', '5s'))) {
                        console.error(...new ExxComError(293884, scriptName, 'MktoForms2 not loaded or not ready').stamp());
                        reject(new Error('MktoForms2 not loaded'));
                        return;
                    }

                    // Additional wait to ensure Marketo is fully initialized
                    // This helps prevent 400 errors on first load
                    if (typeof MktoForms2.whenReady === 'function') {
                        // If whenReady exists, use it
                        await new Promise((readyResolve) => {
                            const readyTimeout = setTimeout(() => readyResolve(null), 2000);
                            MktoForms2.whenReady(() => {
                                clearTimeout(readyTimeout);
                                readyResolve(null);
                            });
                        });
                    } else {
                        // Otherwise just wait a bit for Marketo to stabilize
                        await new Promise((r) => setTimeout(r, 500));
                    }

                    // Set a timeout for form loading
                    let formLoaded = false;
                    const timeout = setTimeout(() => {
                        if (!formLoaded) {
                            console.error(...new ExxComError(293885, scriptName, `Form ${formId} failed to load within 10s`).stamp());
                            reject(new Error('Form load timeout'));
                        }
                    }, 10000);

                    // MktoForms2.loadForm only accepts a success callback, no error callback
                    // Errors are logged to console by Marketo but not exposed to us
                    try {
                        MktoForms2.loadForm(this.config.forms.baseUrl, this.config.munchkin.id, formId, (form: any) => {
                            formLoaded = true;
                            clearTimeout(timeout);
                            if (!form) {
                                console.error(...new ExxComError(293886, scriptName, `Form ${formId} loaded but returned null`).stamp());
                                reject(new Error('Form loaded but is null'));
                                return;
                            }
                            resolve(form);
                        });
                    } catch (loadError) {
                        formLoaded = true;
                        clearTimeout(timeout);
                        console.error(...new ExxComError(293887, scriptName, `Form ${formId} load error: ${loadError}`).stamp());
                        reject(new Error(`Form load failed: ${loadError}`));
                    }
                } catch (err) {
                    console.error(...new ExxComError(293884, scriptName, err).stamp());
                    reject(err);
                }
            })();
        });
    }

    /**
     * @function send
     * @param {String} formId
     * @param {Object} values - The form fields and values
     * @description Sends data to Marketo via a hidden form.
     */
    async send(formId: string, values: any): Promise<void> {
        return new Promise((resolve) => {
            (async () => {
                try {
                    if (!this.isBrowser) {
                        resolve();
                        return;
                    }
                    if (!(await waitFor(() => !!MktoForms2, '5s'))) {
                        console.error(...new ExxComError(938371, scriptName, 'MktoForms2 not loaded').stamp());
                        resolve();
                        return;
                    }
                    const formElem = document.createElement('form');
                    formElem.id = `mktoForm_${formId}`;
                    formElem.style.display = 'none';
                    document.body.appendChild(formElem);
                    MktoForms2.loadForm(this.config.forms.baseUrl, this.config.munchkin.id, formId, (form: any) => {
                        form.addHiddenFields(values);
                        form.onSuccess(() => false); // Prevent form submission from refreshing page
                        form.onSubmit(() => {
                            formElem.remove();
                            resolve();
                        });
                        form.submit();
                    });
                } catch (err) {
                    console.error(...new ExxComError(938371, scriptName, err).stamp());
                    resolve();
                }
            })();
        });
    }
}
